import { normalizeVideoEffects, type VideoEffects } from '@/lib/video-effects/types';
import type { CropCenterKeyframe } from '@/hooks/use-auto-save-crop-keyframes';
import {
  autoCameraMoveTransitions,
  deriveDetectionCropKeyframes,
  toCropCenterKeyframes,
} from '@/lib/crop/deriveDetectionCropKeyframes';
import { buildFacecamSuggestions } from '@/lib/layers/buildFacecamSuggestion';
import { buildLayersRenderInput } from '@/lib/layers/buildLayersRenderInput';
import i18n from 'i18next';
import { normalizeLayer } from '@/lib/layers/normalizeLayer';
import type {
  LayerOverlayItem,
  MediaLayer,
} from '@/lib/layers/types';
import {
  keyframeBoundaries,
  sanitizeKeyframeTransitions,
} from '@/lib/transitions/keyframeCuts';
import {
  sanitizeTransitions,
  type KeyframeTransition,
  type SceneTransition,
} from '@/lib/transitions/types';

export interface PreviewInterval {
  startTime: number;
  endTime: number;
}

export interface ResolvedPersistedClipPreview {
  intervals: PreviewInterval[];
  startTime: number | undefined;
  endTime: number | undefined;
  duration: number;
  hasSavedCropOverride: boolean;
  cropCenterKeyframes: CropCenterKeyframe[];
  videoEffects?: VideoEffects;
  transitions: SceneTransition[];
  keyframeTransitions: KeyframeTransition[];
  /**
   * Persisted keyframe transitions before crop-boundary reconciliation.
   * The editor keeps these while detection-derived keyframes load, then
   * sanitizes them against the effective boundaries at render time.
   */
  rawKeyframeTransitions: KeyframeTransition[];
  mediaLayers: MediaLayer[];
  layers: LayerOverlayItem[];
}

export interface PersistedClipPreviewSource {
  data?: unknown;
  clip_start_time?: unknown;
  clip_end_time?: unknown;
}

interface PersistedSegment {
  start: number;
  end: number;
}

const isFiniteNumber = (value: unknown): value is number => (
  typeof value === 'number' && Number.isFinite(value)
);

const asRecord = (value: unknown): Record<string, unknown> => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {}
);

const resolveSegments = (
  data: Record<string, unknown>,
  fallbackStartTime: number | undefined,
  fallbackEndTime: number | undefined,
): PersistedSegment[] => {
  const saved = Array.isArray(data.segments)
    ? data.segments
      .map(asRecord)
      .filter((segment) => (
        isFiniteNumber(segment.start)
        && isFiniteNumber(segment.end)
        && segment.end > segment.start
      ))
      .map((segment) => ({
        start: segment.start as number,
        end: segment.end as number,
      }))
    : [];

  if (saved.length > 0) return saved;
  if (
    isFiniteNumber(fallbackStartTime)
    && isFiniteNumber(fallbackEndTime)
    && fallbackEndTime > fallbackStartTime
  ) {
    return [{ start: fallbackStartTime, end: fallbackEndTime }];
  }
  return [];
};

const resolveSavedCrop = (
  raw: unknown,
): CropCenterKeyframe[] | null => {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const keyframes = raw
    .map(asRecord)
    .filter((keyframe) => (
      isFiniteNumber(keyframe.time)
      && isFiniteNumber(keyframe.endTime)
      && isFiniteNumber(keyframe.x)
      && isFiniteNumber(keyframe.y)
      && keyframe.endTime >= keyframe.time
    ))
    .map((keyframe) => ({
      time: keyframe.time as number,
      endTime: keyframe.endTime as number,
      x: keyframe.x as number,
      y: keyframe.y as number,
      ...(isFiniteNumber(keyframe.zoom) ? { zoom: keyframe.zoom } : {}),
    }));
  return keyframes.length > 0 ? keyframes : null;
};

const firstDetectionPayload = (sceneDetection: unknown): Record<string, unknown> => {
  const first = Array.isArray(sceneDetection) ? sceneDetection[0] : sceneDetection;
  return asRecord(first);
};

export function resolvePersistedClipPreview({
  clipData,
  fallbackStartTime,
  fallbackEndTime,
  sceneDetection,
  isPortrait,
  projectDuration,
}: {
  clipData: unknown;
  fallbackStartTime?: number;
  fallbackEndTime?: number;
  sceneDetection: unknown;
  isPortrait: boolean;
  projectDuration?: number;
}): ResolvedPersistedClipPreview {
  const data = asRecord(clipData);
  const segments = resolveSegments(data, fallbackStartTime, fallbackEndTime);
  const intervals = segments.map(({ start, end }) => ({
    startTime: start,
    endTime: end,
  }));
  const duration = intervals.reduce(
    (total, interval) => total + interval.endTime - interval.startTime,
    0,
  );
  const startTime = intervals.length > 0
    ? Math.min(...intervals.map((interval) => interval.startTime))
    : undefined;
  const endTime = intervals.length > 0
    ? Math.max(...intervals.map((interval) => interval.endTime))
    : undefined;

  const savedCrop = resolveSavedCrop(data.cropCenterKeyframes);
  const detectionCrop = savedCrop ? [] : deriveDetectionCropKeyframes({
    detection: sceneDetection,
    isPortrait,
  });
  const cropCenterKeyframes = savedCrop ?? toCropCenterKeyframes(detectionCrop);

  const transitions = sanitizeTransitions(data.transitions, intervals.length);
  const rawKeyframeTransitions = Array.isArray(data.keyframeTransitions)
    ? data.keyframeTransitions as KeyframeTransition[]
    : [];
  // While the framing is the detected one, the export glides at detection
  // camera moves; saved transitions come first so one at the same boundary
  // wins. Preview-only: rawKeyframeTransitions (what the editor saves) never
  // holds them.
  const keyframeTransitions = sanitizeKeyframeTransitions(
    [...rawKeyframeTransitions, ...autoCameraMoveTransitions(detectionCrop)],
    keyframeBoundaries(cropCenterKeyframes),
  );

  const storedLayers = Array.isArray(data.editorLayers)
    ? data.editorLayers as MediaLayer[]
    : [];
  const mediaLayers = storedLayers.map(normalizeLayer);
  if (data.editorLayers === undefined) {
    const detection = firstDetectionPayload(sceneDetection);
    mediaLayers.push(
      ...buildFacecamSuggestions({
        facecamUrl: typeof detection.facecam_url === 'string'
          ? detection.facecam_url
          : undefined,
        sourceDurationSec: isFiniteNumber(projectDuration)
          ? projectDuration
          : isFiniteNumber(detection.endTime)
            ? detection.endTime
            : undefined,
        segments,
        facecamSpans: Array.isArray(detection.facecam_spans)
          ? detection.facecam_spans as Array<{ start_time: number; end_time: number }>
          : undefined,
        isPortrait,
      }).map(normalizeLayer),
    );
  }

  return {
    intervals,
    startTime,
    endTime,
    duration,
    hasSavedCropOverride: savedCrop !== null,
    cropCenterKeyframes,
    transitions,
    videoEffects: normalizeVideoEffects(data.videoEffects),
    keyframeTransitions,
    rawKeyframeTransitions,
    mediaLayers,
    layers: buildLayersRenderInput(mediaLayers, duration, { invalidMotionLabel: i18n.t('projectEditor.layers.motionUnavailable', { defaultValue: 'Animation unavailable' }) }),
  };
}

/**
 * Clip-aware entry point shared by route-level previews and the editor.
 * Besides making the legacy top-level range impossible to omit, it merges
 * partial local saves over the query payload instead of dropping untouched
 * composition fields.
 */
export function resolvePersistedClipPreviewForClip({
  clip,
  clipDataOverride,
  sceneDetection,
  isPortrait,
  projectDuration,
}: {
  clip: PersistedClipPreviewSource;
  clipDataOverride?: unknown;
  sceneDetection: unknown;
  isPortrait: boolean;
  projectDuration?: number;
}): ResolvedPersistedClipPreview {
  const savedData = asRecord(clip.data);
  const overrideData = asRecord(clipDataOverride);
  const clipData = clipDataOverride === undefined || clipDataOverride === null
    ? clip.data
    : { ...savedData, ...overrideData };

  return resolvePersistedClipPreview({
    clipData,
    fallbackStartTime: isFiniteNumber(clip.clip_start_time)
      ? clip.clip_start_time
      : undefined,
    fallbackEndTime: isFiniteNumber(clip.clip_end_time)
      ? clip.clip_end_time
      : undefined,
    sceneDetection,
    isPortrait,
    projectDuration,
  });
}

export function buildPreviewProcessingByClipId<
  T extends PersistedClipPreviewSource & { id: string },
>(
  clips: T[],
  {
    sceneDetection,
    isPortrait,
    projectDuration,
  }: {
    sceneDetection: unknown;
    isPortrait: boolean;
    projectDuration?: number;
  },
): Map<string, ResolvedPersistedClipPreview> {
  return new Map(
    clips.map((clip) => [
      clip.id,
      resolvePersistedClipPreviewForClip({
        clip,
        sceneDetection,
        isPortrait,
        projectDuration,
      }),
    ]),
  );
}

export function resolveFirstVisibleClipPreview<
  T extends { id: string; clip_order?: number | null },
>(
  clips: T[],
  hiddenVersionIds: Set<string>,
  previewProcessingByClipId: Map<string, ResolvedPersistedClipPreview>,
): ResolvedPersistedClipPreview | null {
  const first = clips
    .filter((clip) => !hiddenVersionIds.has(clip.id))
    .sort((a, b) => (a.clip_order ?? Infinity) - (b.clip_order ?? Infinity))[0];
  return first ? previewProcessingByClipId.get(first.id) ?? null : null;
}

export function resolveClipCardPreview({
  clip,
  previewProcessing,
  fallbackCropCenterKeyframes,
  projectDuration,
}: {
  clip: PersistedClipPreviewSource;
  previewProcessing?: ResolvedPersistedClipPreview;
  fallbackCropCenterKeyframes?: CropCenterKeyframe[];
  projectDuration?: number;
}): {
  startTime: number;
  endTime: number;
  intervals: PreviewInterval[];
  cropCenterKeyframes: CropCenterKeyframe[] | undefined;
  videoEffects?: VideoEffects;
  transitions: SceneTransition[];
  keyframeTransitions: KeyframeTransition[];
  layers: LayerOverlayItem[];
} {
  const resolved = previewProcessing ?? resolvePersistedClipPreviewForClip({
    clip,
    sceneDetection: null,
    isPortrait: true,
    projectDuration,
  });
  return {
    startTime: resolved.startTime ?? 0,
    endTime: resolved.endTime ?? 0,
    intervals: resolved.intervals,
    cropCenterKeyframes: previewProcessing?.cropCenterKeyframes
      ?? fallbackCropCenterKeyframes,
    transitions: resolved.transitions,
    videoEffects: resolved.videoEffects,
    keyframeTransitions: resolved.keyframeTransitions,
    layers: resolved.layers,
  };
}
